import { Link } from '@tanstack/react-router';
import { KeyRound, ScanFace } from 'lucide-react';
import { useAdminCompanyMembers } from 'app/javascript/queries/useAdmin';
import AdminPanel from 'app/javascript/components/admin/AdminPanel';
import DevicePlatformIcons from 'app/javascript/components/admin/DevicePlatformIcons';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from 'app/javascript/components/ui/table';
import { Badge } from 'app/javascript/components/ui/badge';
import { ROUTES } from 'app/javascript/constants/routes';
import { formatBRDateTime } from 'app/javascript/utils/dates';

const ROLE_LABELS: Record<string, string> = {
  owner: 'dono',
  admin: 'admin',
  member: 'membro',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'pendente',
  accepted: 'aceito',
  rejected: 'recusado',
};

export default function CompanyMembersTable({ slug }: { slug: string }) {
  const { data, isLoading, isError } = useAdminCompanyMembers(slug);
  const members = data ?? [];

  return (
    <AdminPanel
      isLoading={isLoading}
      isError={isError}
      isEmpty={members.length === 0}
      emptyMessage="Esta academia ainda não tem membros."
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Membro</TableHead>
            <TableHead>Papel</TableHead>
            <TableHead>Situação</TableHead>
            <TableHead>Facial</TableHead>
            <TableHead>App</TableHead>
            <TableHead>Entrou em</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map((member) => (
            <TableRow key={member.id}>
              <TableCell className="font-medium">
                <span className="flex flex-wrap items-center gap-2">
                  {member.name ?? '—'}
                  {member.lgpd_erased && <Badge variant="destructive">Removido (LGPD)</Badge>}
                  {!member.claimed && !member.lgpd_erased && (
                    <Badge variant="outline" className="border-amber-500/40 text-amber-400">
                      <KeyRound className="size-3" />
                      não reivindicada
                    </Badge>
                  )}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {member.email ?? 'sem e-mail'}
                </span>
                {member.slug && !member.lgpd_erased && (
                  <Link
                    to={ROUTES.PUBLIC_PROFILE}
                    params={{ slug: member.slug }}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="block text-xs text-muted-foreground transition-colors hover:text-foreground hover:underline"
                  >
                    /p/{member.slug}
                  </Link>
                )}
              </TableCell>
              <TableCell>{ROLE_LABELS[member.role] ?? member.role}</TableCell>
              <TableCell>
                <Badge variant={member.status === 'accepted' ? 'secondary' : 'outline'}>
                  {STATUS_LABELS[member.status] ?? member.status}
                </Badge>
              </TableCell>
              <TableCell>
                {member.face_enrolled ? (
                  <span className="inline-flex items-center gap-1.5 text-sm text-emerald-400">
                    <ScanFace className="size-4" />
                    cadastrada
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell>
                <DevicePlatformIcons devices={member.devices ?? []} />
              </TableCell>
              <TableCell className="whitespace-nowrap text-muted-foreground">
                {member.accepted_at ? formatBRDateTime(member.accepted_at) : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </AdminPanel>
  );
}
